import {
    Box, Container,
    IconButton, Tooltip
} from "@material-ui/core";
import { createTheme, MuiThemeProvider } from "@material-ui/core/styles";
import VisibilityIcon from "@material-ui/icons/Visibility";
import clsx from "clsx";
import MUIDataTable from "mui-datatables";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import LoadingUI from "../../../components/ui/LoadingUI";
import PageTitle from "../../../components/ui/PageTitleUI";
import { useAuth } from "../../../hooks/auth";
import { ShippingRequestMonitorSearch } from "../../../interfaces/ShippingRequest";
import ShippingRequestApi from "../../../services/ShippingRequestApi";
import getDefaultGridOptions from "../../../utils/componentUtils";
import utilsDate from "../../../utils/Date";
import permissions from "../../../utils/Permissions";
import { useStyles } from "./Style";

const ShippingRequestMonitor: React.FC = () => {
    const classes = useStyles();
    const [shippingRequestMonitors, setShippingRequestMonitors] =
        useState<any>();
    const [loading, setLoading] = useState(false);
    const history = useHistory();
    const { user, hasPermission } = useAuth();
    const { t } = useTranslation("ShippingRequest");
    const { t: _t } = useTranslation("General");

    const searchShippingRequestMonitors = () => {
        setLoading(true);

        let filter: ShippingRequestMonitorSearch = {
            companyId: +user.company.companyID,
            plantId: Number(user.company.plantID),
        };

        ShippingRequestApi.searchMonitor(filter)
            .then((result) => {
                if (result && result.items) {
                    setShippingRequestMonitors(result);
                } else {
                    setShippingRequestMonitors([]);
                }
            })
            .catch(() => {
                setShippingRequestMonitors([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!hasPermission(permissions.SHIPPING_REQUEST.Monitor))
            history.replace('NoPermission');
    }, [history, user])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setShippingRequestMonitors([]);
        searchShippingRequestMonitors();
    }, [user]);

    const tableHeadAlignCenter = clsx({
        [classes.tableHeadCenter]: true,
    });

    const columns = [
        {
            name: "id",
            options: {
                display: false,
            },
        },
        {
            name: "protocolNumber",
            label: String(t("protocol")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    className: tableHeadAlignCenter,
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "statusDescription",
            label: String(t("status")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    className: tableHeadAlignCenter,
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "supplierCorporateName",
            label: String(t("supplier")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    className: tableHeadAlignCenter,
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "updatedBy",
            label: String(_t("updatedBy")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    className: tableHeadAlignCenter,
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "updatedAt",
            label: String(_t("updatedAt")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    className: tableHeadAlignCenter,
                }),
                setCellProps: () => ({
                    align: "center",
                }),
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) => {
                    return utilsDate.DateTimeShort(value);
                },
            },
        },
        {
            name: "",
            options: {
                setCellProps: () => ({
                    align: "right",
                    style: {
                        display: "flex",
                    },
                }),
                disableClick: true,
                sort: false,
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) => renderButtons(tableMeta),
            },
        },
    ];

    function view(id: string) {
        history.push(`/ShippingRequest/View/${id}`);
    }

    const renderButtons = (tableMeta: any) => {
        return (
            <>
                <Box display="flex">
                    <Tooltip title={String(_t("view"))}>
                        <IconButton
                            disabled={!hasPermission(permissions.SHIPPING_REQUEST.Edit)}
                            aria-label="View"
                            color="primary"
                            onClick={() => view(tableMeta.rowData[0])}
                        >
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </>
        );
    };

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {},
            },
        },
    });
    return (
        <Container maxWidth={false} component="main">
            <PageTitle title={t("monitorTitle")} />
            <div className={classes.paper}>
                {loading ? (
                    <LoadingUI />
                ) : (
                    <MuiThemeProvider theme={myTheme}>
                        <MUIDataTable
                            title=""
                            data={
                                shippingRequestMonitors &&
                                shippingRequestMonitors?.items
                            }
                            columns={columns}
                            options={{
                                ...getDefaultGridOptions(_t),
                                serverSide: false,
                                pagination: true,
                                sort: true,
                                selectToolbarPlacement: "none",
                                filter: false,
                                search: false,
                                download: false,
                                print: false,
                                viewColumns: false,
                                selectableRows: "none",
                            }}
                        />
                    </MuiThemeProvider>
                )}
            </div>
        </Container>
    );
};
export default ShippingRequestMonitor;
